<template>
  <div class="editor">
    <div v-if="editor" class="toolbar is-flex-direction-row">
      <!-- Bold -->
      <button class="mr-1" @click="toggleBold" :class="{ active: editor.isActive('bold') }" title="Vet">
        <i class="fas fa-bold"></i>
      </button>

      <!-- Italic -->
      <button class="mr-1" @click="toggleItalic" :class="{ active: editor.isActive('italic') }" title="Cursief">
        <i class="fas fa-italic"></i>
      </button>

      <!-- Strikethrough -->
      <button class="mr-1" @click="toggleStrikethrough" :class="{ active: editor.isActive('strike') }" title="Doorhalen">
        <i class="fas fa-strikethrough"></i>
      </button>

      <!-- Underline -->
      <button class="mr-1" @click="toggleUnderline" :class="{ active: editor.isActive('underline') }" title="Onderstrepen">
        <i class="fas fa-underline"></i>
      </button>

      <!-- Headings -->
      <button class="mr-1" @click="setHeading(1)" :class="{ active: editor.isActive('heading', { level: 1 }) }" title="Kop 1">
        <i class="fas fa-heading"></i> 1
      </button>
      <button class="mr-1" @click="setHeading(2)" :class="{ active: editor.isActive('heading', { level: 2 }) }" title="Kop 2">
        <i class="fas fa-heading"></i> 2
      </button>

      <!-- Bullet List -->
      <button class="mr-1" @click="toggleBulletList" :class="{ active: editor.isActive('bulletList') }" title="Ongenummerde lijst">
        <i class="fas fa-list-ul"></i>
      </button>

      <!-- Ordered List -->
      <button class="mr-1" @click="toggleOrderedList" :class="{ active: editor.isActive('orderedList') }" title="Genummerde lijst">
        <i class="fas fa-list-ol"></i>
      </button>

      <!-- Align Left -->
      <button class="mr-1" @click="setAlignment('left')" :class="{ active: editor.isActive({ textAlign: 'left' }) }" title="Links uitlijnen">
        <i class="fas fa-align-left"></i>
      </button>

      <!-- Align Center -->
      <button class="mr-1" @click="setAlignment('center')" :class="{ active: editor.isActive({ textAlign: 'center' }) }" title="Centreren">
        <i class="fas fa-align-center"></i>
      </button>

      <!-- Align Right -->
      <button class="mr-1" @click="setAlignment('right')" :class="{ active: editor.isActive({ textAlign: 'right' }) }" title="Rechts uitlijnen">
        <i class="fas fa-align-right"></i>
      </button>

      <!-- Blockquote -->
      <button class="mr-1" @click="toggleBlockquote" :class="{ active: editor.isActive('blockquote') }" title="Citaat">
        <i class="fas fa-quote-right"></i>
      </button>

      <!-- Codeblock -->
      <button class="mr-1" @click="toggleCodeblock" :class="{ active: editor.isActive('codeBlock') }" title="Codeblok">
        <i class="fas fa-code"></i>
      </button>

      <!-- Table -->
      <button class="mr-1" @click="insertTable" title="Tabel invoegen">
        <i class="fas fa-table"></i>
      </button>

      <!-- Font color -->
      <label class="mr-1" title="Tekstkleur">
        <input type="color" @input="setFontColor($event)" style="width: 2rem; height: 1.3rem;" />
      </label>

      <!-- Link -->
      <button class="mr-1" @click="setLink" title="Link invoegen">
        <i class="fas fa-link"></i>
      </button>

      <!-- Image File Picker -->
      <button class="mr-1" @click="openFilePicker" title="Afbeelding invoegen">
        <i class="fas fa-image"></i>
      </button>
      <input type="file" ref="fileInput" style="display: none;" @change="handleImageSelect" accept="image/*" />

      <!-- Button to embed YouTube -->
      <button class="mr-1" @click="embedYouTubeVideo" title="Voeg YouTube video in">
        <i class="fas fa-video"></i>
      </button>

      <!-- Strip Markup -->
      <button class="mr-1" @click="stripMarkup" title="Opmaak verwijderen">
        <i class="fas fa-eraser"></i>
      </button>
    </div>

    <EditorContent v-if="editor" :editor="editor" />
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import { Underline } from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { TextAlign } from "@tiptap/extension-text-align";
import { Color } from "@tiptap/extension-color";
import { Link } from "@tiptap/extension-link";
import { Youtube } from "@tiptap/extension-youtube";

export default {
  components: {
    EditorContent,
  },
  props: {
    initialContent: {
      type: String,
      default: '<p></p>',
    },
  },
  data() {
    return {
      editor: null,
    }
  },
  mounted() {
    this.editor=new Editor({
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3]
          },
          blockquote: true,
        }),
        TextStyle.configure({ mergeNestedSpanStyles: true }),
        Underline,
        TextAlign.configure({
          types: ['paragraph', 'heading'],
        }),
        Image,
        Table.configure({
          resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
        Color.configure({
          types: ['textStyle'],
        }),
        Link.configure({
          protocols: ['https'],
          defaultProtocol: 'https',
        }),
        Youtube
      ],
      content: this.initialContent,
      onUpdate: ({ editor }) => {
        const html=editor.getHTML()
        this.$emit('update:content', html)
      },
    })
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy()
    }
  },
  methods: {
    toggleBold() {
      this.editor.chain().focus().toggleBold().run()
    },
    toggleItalic() {
      this.editor.chain().focus().toggleItalic().run()
    },
    toggleStrikethrough() {
      this.editor.chain().focus().toggleStrike().run()
    },
    toggleUnderline() {
      this.editor.chain().focus().toggleUnderline().run()
    },
    setHeading(level) {
      this.editor.chain().focus().toggleHeading({ level }).run()
    },
    toggleBulletList() {
      this.editor.chain().focus().toggleBulletList().run()
    },
    toggleOrderedList() {
      this.editor.chain().focus().toggleOrderedList().run()
    },
    setAlignment(alignment) {
      this.editor.chain().focus().setTextAlign(alignment).run()
    },
    toggleBlockquote() {
      this.editor.chain().focus().toggleBlockquote().run()
    },
    toggleCodeblock() {
      this.editor.chain().focus().toggleCodeBlock().run()
    },
    insertTable() {
      // Create a table with 3 rows and 3 columns as a valid structure
      this.editor.chain().focus().insertTable({
        rows: 3,
        cols: 3
      }).run()
    },
    setFontColor(event) {
      this.editor.chain().focus().setColor(event.target.value).run()
    },
    setLink() {
      const url = prompt('Enter the URL:')
      if (url) {
        this.editor.chain().focus().setLink({ href: url }).run()
      }
    },
    openFilePicker() {
      // Trigger file input click
      this.$refs.fileInput.click()
    },

    handleImageSelect(event) {
      const file = event.target.files[0]
      if (!file) return

      const reader = new FileReader()

      reader.onloadend = () => {
        const base64Image = reader.result

        // Insert image using Image extension's setImage method
        this.editor.chain().focus().setImage({ src: base64Image }).run()
      }

      reader.readAsDataURL(file)
    },
    embedYouTubeVideo() {
      const videoUrl = prompt('Kopieer de YouTube video URL')

      if (videoUrl) {
        this.editor.chain().focus().setYoutubeVideo({ src: videoUrl }).run()
      }
    },
    stripMarkup() {
      this.editor.chain().focus().clearNodes().run()  // This will strip markup
    }
  },
}
</script>

<style scoped>
.editor {
  border: 1px solid #ccc;
  padding: 10px;
}
.toolbar {
  margin-bottom: 10px;
}
.toolbar button {
  margin-right: 5px;
  cursor: pointer;
}
button.active {
  background-color: #eee;
}

</style>

<style>
.editorWrapper h1 {
  font-size: 2rem;
  font-weight: bold;
}

.editorWrapper h2 {
  font-size: 1.5rem;
  font-weight: bold;
}

.editorWrapper ul {
  list-style: disc inside;
  padding-left: 1rem;
  margin: 0.5rem 0;
}

.editorWrapper ol {
  list-style-position: inside;
  padding-left: 1rem;
  margin: 0.5rem 0;
}

.editorWrapper li > p {
  display: inline;
  margin: 0;
}

.editorWrapper table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
}

.editorWrapper td, .editorWrapper th {
  border: 1px solid #ccc;
  padding: 10px;
  text-align: center;
}

.editorWrapper th {
  background-color: #f4f4f4;
}

.editorWrapper blockquote {
  border-left: 4px solid #ccc;
  margin: 1rem 0;
  padding-left: 1rem;
  color: #555;
  font-style: italic;
}
</style>
